<template>
  <div class="teacher-container">
    <el-row>
      <el-col :span="isGeneralAdmin || hasFilter ? 12 : 24">
        <organization-breadcrumb :items="[$t($i18nLabelsHelper(currentOrganizationType, 'teachers_page.title'))]" />
      </el-col>
      <el-col v-if="isGeneralAdmin || hasFilter" :span="12">
        <admin-organization-switcher/>
      </el-col>
    </el-row>
    <base-table-loading
      v-if="loading"
      :data="skeletonData"
    />
    <elsa-table-layout v-else
      :current-page.sync="currentPage"
      :currentChange="getTeachersPage"
      :inviteButtonLabel="$t($i18nLabelsHelper(currentOrganizationType, 'teachers_page.invite'))"
      :pageSize="numberElementsPerPage"
      :pager-counter="pageCounter"
      :pendingInviteUsers="pendingInviteUsersPage"
      :pendingUsersCurrentPage.sync="pendingInviteUsersCurrentPage"
      :query-placeholder="$t('common.search')"
      :queryString.sync="queryString"
      :screen="getCurrentScreen"
      :search="searchTeachers"
      :title="$t($i18nLabelsHelper(currentOrganizationType, 'teachers_page.title'))"
      :total="teachersCount"
      :totalPendingUsers="pendingUsersCount"
      v-on:invite-teacher="modals.showInviteTeacherModal = true"
      v-show="!inviteMode"
    >
      <template v-slot:drop-down>
        <span @click="inviteMode = 'email-invite'" class="action-item invite-users">
          <i class="elsa-icon-email" />
          {{ $t('organizations.users_section.invite_by_email') }}
        </span>
        <span @click="inviteMode = 'import-users'" class="action-item import-users">
          <i class="elsa-icon-import" />
          {{ $t('organizations.users_section.import_users') }}
        </span>
      </template>
      <teachers-table :data="teachersData" :handle-remove="removeTeacher" :handle-update="editLearner" />
    </elsa-table-layout>
    <edit-organization-learner 
      :dialogVisible="modals.showEditLearnerModal" 
      v-on:closed="modals.showEditLearnerModal = false" 
      :organizationType="currentOrganizationType" 
      @updateLearner="updateLearner"
      :userEdited="userEdited"
      :title="$t('organization_profile.admin_actions.edit_user')"
    />
    <delete-organization-user :dialogVisible="modals.showRemoveAccountModal" @doRemoveStudentFromOrg="handelRemoveFromOrganization" v-on:closed="modals.showRemoveAccountModal = false" />
    <invite-teachers :dialogVisible="modals.showInviteTeacherModal" :inviteUsers="handleInviteUsers" :organizationId="currentOrganizationId" :organizationType="currentOrganizationType" v-on:closed="modals.showInviteTeacherModal = false" />
  </div>
</template>

<script>
import ElsaTableLayout from '@/components/common/ElsaTableLayout'
import TeachersTable from '@/components/teachers/TeachersTable'
import OrganizationBreadcrumb from '@/components/common/OrganizationBreadcrumb'
import DeleteOrganizationUser from '@/components/modals/organizations/DeleteOrganizationUsers'
import InviteTeachers from '@/components/modals/organizations/InviteTeachers'
import { TEACHER } from '@/constants/userRoles'
import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER } from '@/constants/generic'
import { successMessage, warningMessage } from '@/utils/messages'
import { mapGetters, mapActions } from 'vuex'
import { pathOr, always } from 'ramda'
import AdminOrganizationSwitcher from "@/components/dashboard/AdminOrganizationSwitcher";
import EditOrganizationLearner from '@/components/modals/organizations/EditOrganizationLearner'
import BaseTableLoading from '@/components/common/BaseTableLoading.vue';

export default {
  components: {
    BaseTableLoading,
    ElsaTableLayout,
    TeachersTable,
    OrganizationBreadcrumb,
    DeleteOrganizationUser,
    InviteTeachers,
    AdminOrganizationSwitcher,
    EditOrganizationLearner
  },
  data() {
    return {
      currentPage: 1,
      pendingInviteUsersCurrentPage: 1,
      inviteMode: false,
      queryString: null,
      modals: {
        showInviteTeacherModal: false,
        showAddLearnerToTeamModal: false,
        showRemoveAccountModal: false,
        showEditLearnerModal: false
      },
      userEdited: {
        id: '',
        first_name: '',
        last_name: '',
        roles: []
      },
      loading: true,
      skeletonData: [
        {
          name: "Teacher",
          label: "Name",
          layout: [
            {
              width: "30%",
              justifyContent: "center",
              alignItems: "center",
              items: [
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                },
              ],
            },
            {
              width: "70%",
              items: [
                {
                  type: "rectangle",
                  width: "60%",
                  height: "12px",
                },
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Accepted Leaners",
          label: "No. Of Classes",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Total Learners",
          label: "No. Of Leaners",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Start Date",
          label: "",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        }
      ]
    }
  },
  props: ['new', 'organizationId'],
  methods: {
    searchTeachers(search) {
      return this.getTeachers({ search })
    },
    handleInviteUsers(emails, first_names, last_names, _, consent) {
      const organizationId = this.currentOrganizationId

      return this.inviteUsers({
        organizationId,
        emails,
        first_names,
        last_names,
        role: TEACHER,
        consent
      }).then(async () => {
        always(this.getTeachers({ page: this.currentPage }))
        always(this.getPendingInviteTeachers())
      })
    },
    getTeachersPage(page) {
      return this.getTeachers({ page })
    },
    editLearner(learner) {
      this.modals.showEditLearnerModal = true
      this.userEdited = {
        id: learner.id,
        first_name: learner.first_name,
        last_name: learner.last_name,
        roles: learner.roles
      }
    },
    removeTeacher(learner_id) {
      this.modals.showRemoveAccountModal = true
      this.userIdRemoved = learner_id
    },
    updateLearner(user) {
      this.updateOrganizationUserInfo({ userId: user.id, organizationId: this.organizationId, first_name: user.first_name, last_name: user.last_name, roles: user.roles })
        .then(() => {
          const message = this.$i18nLabelsHelper(this.$store.getters['organization/currentOrganizationType'], 'update_teacher_of_organization_success')
          successMessage(this.$t(message))(this)
          this.getTeachersPage(this.currentPage)
        })
        .catch((error) => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, 'update_teacher_of_organization_warning')
          return warningMessage(this.$t(message))(this)
        })
    },
    handelRemoveFromOrganization(isRemoveUserFromSystem = false) {
      this.removeFromOrganization({ userId: this.userIdRemoved, organizationId: this.organizationId, isRemoveUserFromSystem })
        .then(() => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, 'remove_from_organization_success')
          successMessage(this.$t(message))(this)
          this.getTeachersPage(this.currentPage)
        })
        .catch((error) => {
          const message = this.$i18nLabelsHelper(this.currentOrganizationType, 'remove_from_organization_warning')
          return warningMessage(this.$t(message))(this)
        })
    },
    ...mapActions('invite', ['inviteUsers']),
    ...mapActions('teachers', ['getTeachers', 'setCurrentTeacher']),
    ...mapActions('organization', ['getPendingInviteTeachers']),
    ...mapActions({
      removeFromOrganization: 'organization/removeFromOrganization',
      updateOrganizationUserInfo: 'organization/updateOrganizationUserInfo'
    })
  },
  computed: {
    teachersData() {
      return this.teachers
    },
    getCurrentScreen() {
      return TEACHER
    },
    pageCounter: always(PAGE_COUNTER),
    numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE),
    teachersCount: pathOr(0, ['paginateTeachers', 'count']),
    teachers: pathOr([], ['paginateTeachers', 'results']),
    ...mapGetters('teachers', ['paginateTeachers']),
    ...mapGetters('organization', ['currentOrganizationId']),
    ...mapGetters('organization', [
      'pendingUsersCount', 
      'pendingInviteUsersPage', 
      'currentOrganizationType',
      'isGeneralAdmin',
      'hasFilter',
      ]),
    ...mapGetters({
      currentOrganizationType: 'organization/currentOrganizationType',
      currentOrganizationId: 'organization/currentOrganizationId'
    })
  },
  async mounted() {
    try {
      this.loading = true
      await this.getTeachers()
      await this.getPendingInviteTeachers()
    } finally {
      this.loading = false
    }
  },
  watch: {
    new: {
      immediate: true,
      handler(newValue) {
        this.inviteMode = newValue
      }
    },
    pendingInviteUsersCurrentPage(page) {
      return this.getPendingInviteTeachers({ page })
    }
  }
}
</script>

<style lang="scss" scoped>
.teacher-container {
  width: 100%;
}
</style>
